import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  Paper
} from '@mui/material';
import { useConsent } from '../hooks/useConsent';
import { scriptManager } from '../utils/ScriptManager';
import CookieBanner from '../components/CookieBanner';
import GDPRConsentModal from '../components/GDPRConsentModal';

const CookieDemoPage: React.FC = () => {
  const { 
    hasValidConsent, 
    hasPermission, 
    clearConsent, 
    consentStatus,
    error 
  } = useConsent();
  
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const handleForceShowBanner = () => {
    clearConsent();
    setShowBanner(true);
  };

  const getPermissionStatus = (permission: 'required' | 'analytics' | 'marketing') => {
    if (!hasValidConsent) return 'Keine Einwilligung';
    return hasPermission(permission) ? 'Erlaubt' : 'Blockiert';
  };

  const getPermissionColor = (permission: 'required' | 'analytics' | 'marketing') => {
    if (!hasValidConsent) return 'default';
    return hasPermission(permission) ? 'success' : 'error';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" gutterBottom align="center">
        🍪 Cookie-Banner Demo
      </Typography>

      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        Demonstration des DSGVO-konformen Cookie-Banners für Smart Storage Checker
      </Typography>

      {/* Status Übersicht */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          📊 Aktueller Consent-Status
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Einwilligung Status
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={hasValidConsent ? '✅ Gültige Einwilligung' : '❌ Keine Einwilligung'}
                    color={hasValidConsent ? 'success' : 'error'}
                    size="medium"
                  />
                </Box>
                
                {hasValidConsent && consentStatus.expiresAt && (
                  <Typography variant="body2" color="text.secondary">
                    Gültig bis: {new Date(consentStatus.expiresAt).toLocaleDateString('de-DE')}
                  </Typography>
                )}
                
                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Cookie-Kategorien
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">🔧 Notwendige Cookies:</Typography>
                    <Chip 
                      label={getPermissionStatus('required')}
                      color={getPermissionColor('required')}
                      size="small"
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">📊 Statistik & Analyse:</Typography>
                    <Chip 
                      label={getPermissionStatus('analytics')}
                      color={getPermissionColor('analytics')}
                      size="small"
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">📢 Marketing & Kontakt:</Typography>
                    <Chip 
                      label={getPermissionStatus('marketing')}
                      color={getPermissionColor('marketing')}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Script-Manager Debug */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          🛠️ Script-Manager Debug
        </Typography>
        
        <Button 
          variant="outlined" 
          onClick={() => console.log('Script-Manager Debug:', scriptManager.getDebugInfo())}
          sx={{ mb: 2 }}
        >
          Debug Info in Konsole ausgeben
        </Button>
        
        <Typography variant="body2" color="text.secondary">
          Öffnen Sie die Browser-Konsole (F12) für detaillierte Script-Loading-Informationen.
        </Typography>
      </Paper>

      {/* Aktionen */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          🎮 Demo-Aktionen
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="contained" 
              color="primary"
              fullWidth
              onClick={handleForceShowBanner}
              disabled={!hasValidConsent}
            >
              Cookie-Banner anzeigen
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="contained" 
              color="secondary"
              fullWidth
              onClick={() => setShowDetailModal(true)}
            >
              Detail-Modal öffnen
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              color="error"
              fullWidth
              onClick={clearConsent}
              disabled={!hasValidConsent}
            >
              Einwilligung löschen
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => window.location.reload()}
            >
              Seite neu laden
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Funktions-Beschreibung */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          📋 Funktionen
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Cookie-Banner Features:
            </Typography>
            <ul>
              <li>Kompaktes Overlay unten links</li>
              <li>Animierte Ein-/Ausblendung</li>
              <li>Schnell-Buttons: "Alle akzeptieren" / "Nur notwendige"</li>
              <li>Erweiterte Einstellungen mit kategorie-spezifischen Schaltern</li>
              <li>DSGVO-konforme Speicherung mit Zeitstempel und Version</li>
              <li>Responsive Design für Desktop und Mobile</li>
            </ul>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Script-Blockierung:
            </Typography>
            <ul>
              <li>Automatische Erkennung von Analytics-Scripts</li>
              <li>Marketing-Script-Blockierung</li>
              <li>Notwendige Scripts immer erlaubt</li>
              <li>DOM-Interception für dynamische Script-Erstellung</li>
              <li>Consent-basiertes Loading</li>
              <li>Debug-Informationen verfügbar</li>
            </ul>
          </Grid>
        </Grid>
      </Paper>

      {/* Cookie-Banner */}
      {(showBanner || !hasValidConsent) && (
        <CookieBanner onOpenDetailModal={() => setShowDetailModal(true)} />
      )}

      {/* Detail-Modal */}
      <GDPRConsentModal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onAccept={() => setShowDetailModal(false)}
        onDecline={() => setShowDetailModal(false)}
      />
    </Container>
  );
};

export default CookieDemoPage;